import { useCallback, useEffect, useState } from 'react'
import {
  getQualificationNotificationSettings,
  updateQualificationNotificationSettings,
} from '../services/qualificationNotificationSettingsApi'
import type { QualificationNotificationMode, QualificationNotificationSettings } from '../types'

// `enabled` lets a caller skip the fetch entirely (a member is forbidden from
// GET .../qualification-notification-settings) without violating the rules of
// hooks by conditionally calling this hook — same shape as useMemorySettings.
export function useQualificationNotificationSettings(workspaceId: string, enabled = true) {
  const [settings, setSettings] = useState<QualificationNotificationSettings | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!enabled) {
      setLoading(false)
      return () => {}
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    getQualificationNotificationSettings(workspaceId)
      .then((s) => { if (!cancelled) setSettings(s) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load notification settings') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [workspaceId, enabled])

  useEffect(() => load(), [load])

  // Saves immediately on selection, same as memory's per-tier toggle — no
  // page-level Save button for this setting.
  const setMode = useCallback(async (mode: QualificationNotificationMode) => {
    const previous = settings
    setSettings((prev) => (prev ? { ...prev, mode } : prev))
    setSaveError(null)
    setSaving(true)
    try {
      const resolved = await updateQualificationNotificationSettings(workspaceId, mode)
      setSettings(resolved)
    } catch (err) {
      setSettings(previous)
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [workspaceId, settings])

  return { settings, loading, error, retry: load, saving, saveError, setMode }
}
